import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { useRequestsContext } from "@/contexts/RequestsContext";
import { useSnackbar } from "@/contexts/SnackbarContext";
import { backendService } from "@/services/backend.service";
import { showAgencyPostingResult } from "@/utils/agencyWarnings";
import { trackEvent } from "@/utils/analytics";
import type { Request } from "@/utils/models";

export const useUpdateRequest = () => {
	const { showErrorSnackbar } = useHospitalData();

	return useMutation({
		mutationFn: async ({ departmentId, request }: { departmentId: string; request: Request }) => {
			const response = await backendService.updateRequest(departmentId, request);
			return response.data;
		},
		onError: (error, variables) =>
			showErrorSnackbar(
				"Failed to update request",
				{
					request_id: variables.request.id,
					departmentId: variables.departmentId,
				},
				error,
			),
	});
};

export const useDeleteShift = () => {
	const { showErrorSnackbar, chosenDepartment } = useHospitalData();
	const { showSnackbar } = useSnackbar();
	const { refreshRequests } = useRequestsContext();
	const { t } = useTranslation();

	return useMutation({
		mutationFn: async ({ shiftId }: { shiftId: string }) => {
			const response = await backendService.deleteShift(shiftId, chosenDepartment.department_id);
			return response.data;
		},
		onSuccess: async (data, variables) => {
			showAgencyPostingResult(showSnackbar, t("success.deleted.open.shift"), t, data?.agency_posting_failures);
			trackEvent("Staffing - request deleted", {
				source: "requests_tab",
				request_id: variables.shiftId,
			});
			await refreshRequests();
		},
		onError: (error, variables) => {
			showErrorSnackbar("Failed to delete open shift", { action: "deleted open shift failed", shift_id: variables.shiftId }, error);
		},
	});
};

type UpdateNursesNeededParams = { handleClose?: () => void };

export const useUpdateNursesNeeded = ({ handleClose }: UpdateNursesNeededParams) => {
	const { showSnackbar } = useSnackbar();
	const { refreshRequests } = useRequestsContext();
	const { showErrorSnackbar } = useHospitalData();
	const { t } = useTranslation();

	return useMutation({
		mutationFn: async ({ shiftId, neededNurses }: { shiftId: string; neededNurses: number }) => backendService.patchShift(shiftId, neededNurses),
		onSuccess: async (response) => {
			showAgencyPostingResult(showSnackbar, t("success.needed.nurses.updated"), t, response?.data?.agency_posting_failures);
			await refreshRequests();
			handleClose?.();
		},
		onError: (error, variables) =>
			showErrorSnackbar("Failed to update needed staff", { action: "updated needed nurses failed", shift_id: variables.shiftId }, error),
	});
};
